// The consent bar and Microsoft Clarity, the company's way (BS.Analytics.Client's analytics.js): the product site's public pages write this module
// only while a Clarity project id is configured (Web/SiteAnalytics.cs). Its settings are data attributes of its own <script> element - the pages'
// Content-Security-Policies run no inline script - and it hands them to the package's startPage, which shows the bar, loads Clarity only after
// "Akzeptieren"/"Accept", and opens the bar again from every data-bs-consent-settings element (the footer's cookie settings) so a visitor can
// withdraw. This file stores nothing, sets no cookie and sends nothing itself; it only imports the package's module from our own origin.

/** The consent bar's texts as analytics.js takes them. */
export interface ConsentTexts {
  text: string;
  accept: string;
  decline: string;
  privacyText: string | null;
  privacyHref: string | null;
  label: string | null;
  language: string | null;
}

/** What analytics.js's startPage takes, as far as this site uses it: Clarity and the bar, no Google Tag Manager, no ad click ids. */
export interface PageOptions {
  gtmContainerId: null;
  configUrl: null;
  clarityProjectId: string;
  banner: ConsentTexts;
  attributionParameters: string[];
  adClickParameters: Record<string, string>;
}

/** The package's module. */
export interface AnalyticsModule {
  startPage(options: PageOptions): Promise<void>;
}

/** Where the settings stand. */
export const SELECTOR = "script[data-bs-clarity]";

/** A path on our own origin ("/x", never "//host" or "/\host"). */
function ownPath(value: string | undefined): string | null {
  return value && value.charAt(0) === "/" && value.charAt(1) !== "/" && value.charAt(1) !== "\\" ? value : null;
}

/** The module's address and the page's options, or null when the element or one of the required values is missing. */
export function readSettings(element: HTMLElement | null): { module: string; options: PageOptions } | null {
  if (!element) {
    return null;
  }
  const data = element.dataset;
  const module = ownPath(data.module);
  const id = data.bsClarity || "";
  if (!module || !/^[a-z0-9]{6,32}$/.test(id) || !data.text || !data.accept || !data.decline) {
    return null;
  }
  return {
    module: module,
    options: {
      gtmContainerId: null,
      configUrl: null,
      clarityProjectId: id,
      banner: {
        text: data.text,
        accept: data.accept,
        decline: data.decline,
        privacyText: data.privacyText || null,
        privacyHref: ownPath(data.privacyHref),
        label: data.label || null,
        language: data.language || null,
      },
      attributionParameters: [],
      adClickParameters: {},
    },
  };
}

/** Loads the package's module through load (passed in, so a test can hand in its own) and starts it. */
export async function start(
  settings: { module: string; options: PageOptions },
  load: (url: string) => Promise<AnalyticsModule>,
): Promise<void> {
  const analytics = await load(settings.module);
  await analytics.startPage(settings.options);
}

const settings = readSettings(document.querySelector<HTMLElement>(SELECTOR));
if (settings) {
  void start(settings, (url) => import(url) as Promise<AnalyticsModule>);
}
